import React, {useCallback, useMemo, useState} from 'react';
import {Dropdown} from 'react-bootstrap';
import OutsideClickHandler from 'shared/components/Header/OutsideClickHandler';
import type {TableButton, TableDropdown, TableRowType} from 'shared/types';
import {getActionLabel} from './helpers';

export const JobCardPrintAction = ({
    button,
    row,
}: {
    button: TableButton;
    row: TableRowType;
}) => {
    const [show, setShow] = useState(false);
    const onOutsideClick = useCallback(() => setShow(false), []);
    const toggleDropdown = useCallback(() => setShow((prev) => !prev), []);

    const options = useMemo(() => {
        const {dynamicOptions, staticOptions} = (button.dropdown || []).reduce(
            (acc, option) => {
                if (option.source) {
                    acc.dynamicOptions.push(option);
                } else {
                    acc.staticOptions.push(option);
                }
                return acc;
            },
            {
                dynamicOptions: [] as TableDropdown[],
                staticOptions: [] as TableDropdown[],
            }
        );

        const allOptions = [...staticOptions];

        dynamicOptions.forEach((option) => {
            const fieldOptions = row[option.source] as unknown as {
                [key: string]: string | number;
            }[];

            fieldOptions
                ?.filter(
                    (fieldOption) =>
                        !option.type || option.type === fieldOption.type
                )
                .forEach((fieldOption) => {
                    const newOption: TableDropdown = {
                        name: String(fieldOption.name),
                        show: option.show,
                        order: option.order,
                    };

                    if (typeof option.action === 'function') {
                        newOption.action = () =>
                            option.action(
                                Number(
                                    fieldOption[
                                        option.identifier as keyof typeof fieldOption
                                    ]
                                )
                            );
                    }

                    allOptions.push(newOption);
                });
        });

        return allOptions.sort((current, next) => current.order - next.order);
    }, [button, row]);

    const onItemClick = useCallback(
        (option: TableDropdown) => (event: React.MouseEvent<HTMLElement>) => {
            if (typeof option.action === 'function') {
                option.action();
            } else {
                button.action?.(row, event, option.name);
            }
        },
        [button, row]
    );

    return (
        <OutsideClickHandler
            className="job-card-action job-card-action-print"
            onOutsideClick={onOutsideClick}>
            <Dropdown
                className="job-card-action-print-dropdown"
                show={show}
                onToggle={toggleDropdown}>
                <Dropdown.Toggle as="span" role="table-option">
                    {getActionLabel(button.title)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {options.map((option) => {
                        if (option.hasOwnProperty('show')) {
                            if (
                                typeof option.show === 'boolean' &&
                                !option.show
                            ) {
                                return null;
                            }

                            if (
                                typeof option.show === 'function' &&
                                !option.show(row)
                            ) {
                                return null;
                            }
                        }

                        return (
                            <Dropdown.Item
                                key={option.name}
                                onClick={onItemClick(option)}>
                                {option.name}
                            </Dropdown.Item>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown>
        </OutsideClickHandler>
    );
};
